import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { User, Mail, Phone, Lock, UserPlus, Info, X } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    marketing_consent: false,
    terms_accepted: false
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData.first_name || !formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    // Last name validation
    if (!formData.last_name || !formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    // Email validation
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    }

    // Confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms acceptance
    if (!formData.terms_accepted) {
      newErrors.terms_accepted = 'You must accept the terms and privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    const { confirmPassword, ...registrationData } = formData;
    const result = await register(registrationData);

    if (result.success) {
      toast.success('Registration successful! Welcome to NexSite.');
      navigate('/');
    } else {
      // Show error message
      toast.error(result.error);
      
      // Try to parse and display field-specific errors
      const errorMsg = result.error.toLowerCase();
      const newErrors = {};
      
      if (errorMsg.includes('email')) {
        newErrors.email = result.error;
      }
      if (errorMsg.includes('password')) {
        newErrors.password = result.error;
      }
      if (errorMsg.includes('phone')) {
        newErrors.phone = result.error;
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
      }
    }

    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={() => navigate('/')}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col" hideCloseButton={true}>
        {/* Fixed Header */}
        <DialogHeader className="px-6 py-3 border-b flex-shrink-0 relative flex items-center">
          <DialogTitle className="text-lg text-center flex-1">Register</DialogTitle>
          <button
            onClick={() => navigate('/')}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </DialogHeader>

        {/* Scrollable Form Content - Hidden Scrollbar */}
        <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-hide">
          <form id="register-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="first_name" className="text-sm">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className={`pl-9 h-9 text-sm ${errors.first_name ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.first_name && (
                    <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="last_name" className="text-sm">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className={`pl-9 h-9 text-sm ${errors.last_name ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.last_name && (
                    <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`pl-9 h-9 text-sm ${errors.email ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-sm">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className={`pl-9 h-9 text-sm ${errors.phone ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.phone && (
                  <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="password" className="text-sm">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-gray-400 hover:text-gray-600 transition-colors">
                          <Info className="h-4 w-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-3" side="right">
                        <p className="text-sm font-medium mb-2">Password Requirements:</p>
                        <ul className="text-xs space-y-1.5">
                          <li className="flex items-center">
                            <span className={`mr-2 ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`}>
                              {formData.password.length >= 8 ? '✓' : '•'}
                            </span>
                            At least 8 characters
                          </li>
                          <li className="flex items-center">
                            <span className={`mr-2 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`}>
                              {/[A-Z]/.test(formData.password) ? '✓' : '•'}
                            </span>
                            At least one uppercase letter
                          </li>
                          <li className="flex items-center">
                            <span className={`mr-2 ${/\d/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`}>
                              {/\d/.test(formData.password) ? '✓' : '•'}
                            </span>
                            At least one number
                          </li>
                        </ul>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className={`pl-9 h-9 text-sm ${errors.password ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-500 mt-1">{errors.password}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-sm">
                    Confirm Password <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className={`pl-9 h-9 text-sm ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3 border-t pt-3">
                <div className="flex items-start space-x-2.5">
                  <Checkbox
                    id="marketing_consent"
                    name="marketing_consent"
                    checked={formData.marketing_consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, marketing_consent: checked })}
                    className="mt-0.5"
                  />
                  <label htmlFor="marketing_consent" className="text-xs text-gray-700 cursor-pointer leading-relaxed">
                    Enable SMS 2 Factor Authentication for Logging in <span className="text-gray-500">(Optional)</span>
                  </label>
                </div>

                <div>
                  <div className="flex items-start space-x-2.5">
                    <Checkbox
                      id="terms_accepted"
                      name="terms_accepted"
                      checked={formData.terms_accepted}
                      onCheckedChange={(checked) => setFormData({ ...formData, terms_accepted: checked })}
                      className={`mt-0.5 ${errors.terms_accepted ? 'border-red-500' : ''}`}
                    />
                    <label htmlFor="terms_accepted" className="text-xs text-gray-700 cursor-pointer leading-relaxed">
                      <span className="text-red-500">* </span>
                      I accept the{' '}
                      <Link to="/terms" target="_blank" className="text-orange-500 hover:text-orange-600 underline">
                        Terms of Service
                      </Link>
                      {' '}&{' '}
                      <Link to="/privacy" target="_blank" className="text-orange-500 hover:text-orange-600 underline">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  {errors.terms_accepted && (
                    <p className="text-xs text-red-500 mt-1 ml-6">{errors.terms_accepted}</p>
                  )}
                </div>
              </div>

              {/* Compliance Text */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4">
                <p className="text-xs text-gray-600 leading-relaxed">
                  <span className="font-medium">SMS Two-Factor Authentication (Optional):</span> We highly recommend you to enable 2 Factor Authentication for security purposes. If you enable 2FA, we will send verification codes to your phone number when you log in. 
                  Msg & data rates may apply. This is optional and not required to create an account. 
                  Reply HELP for help or STOP to cancel.
                </p>
              </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <DialogFooter className="px-6 py-4 border-t flex-shrink-0 flex-col gap-3 sm:flex-col">
          <Button
            type="submit"
            form="register-form"
            disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white h-9 text-sm"
          >
            {loading ? (
              'Creating Account...'
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Create Account
              </>
            )}
          </Button>

          <div className="text-center text-xs">
            <span className="text-gray-600">Already have an account? </span>
            <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium">
              Sign in
            </Link>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Register;
